import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { ref, push, set, query, orderByChild, equalTo, get } from 'firebase/database';
import MobileLayout from '../../components/mobile/MobileLayout';

const companyName  = process.env.REACT_APP_COMPANY_NAME  || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
const API_BASE     = process.env.NODE_ENV==='development' ? '' : (process.env.REACT_APP_API_URL||'');

/* ─── Types ──────────────────────────────────────────────────────── */
interface Ticket {
  id?: string;
  name: string;
  email: string;
  subject: string;
  priority: string;
  message: string;
  userId: string|null;
  status: 'open'|'closed';
  createdAt: number;
}

/* ─── FAQ data (same as desktop Support.tsx) ─────────────────────── */
const faqs = [
  { q: 'Why is my bot not responding?',
    a: "For WhatsApp bots: check your webhook is verified in Meta Developer Console and your access token hasn't expired. For website bots: make sure the embed script is correctly placed before the </body> tag. Check the browser console for errors." },
  { q: 'How do I update my WhatsApp access token?',
    a: 'Access tokens expire every 60 days. Go to Meta Developer Console → Your App → WhatsApp → API Setup → Generate new token. Then in Autopilot, go to Dashboard → select your bot → Settings → paste the new token.' },
  { q: 'How do I connect my website bot?',
    a: 'In the Dashboard, select your website bot, then go to the Embed Code tab. Copy the script tag and paste it before the </body> tag on your website. It works on WordPress, Wix, Squarespace, and any HTML site.' },
  { q: 'Can I have different bots for different channels?',
    a: 'Yes! You can create separate bots for WhatsApp, your website, Facebook, and voice — each with its own flow. They all appear in your dashboard sidebar under "My Bots".' },
  { q: 'How do I load a template into my bot?',
    a: 'Open the Flow Builder for your bot and click "📋 Load template" in the top bar. A panel shows templates for that bot type. Select one and confirm to load it. Note: this replaces your current flow, so save first.' },
  { q: 'Can I upgrade my plan?',
    a: 'Yes. Go to Billing in the sidebar, choose a new plan, and complete payment. The change takes effect immediately.' },
  { q: 'How do I export conversation data?',
    a: 'In the Dashboard, select a bot and go to the Conversations tab. Use the filters to narrow down the data, then click "Export CSV". For daily stats, use the Reports tab → Download CSV.' },
  { q: "What if a customer types something my bot doesn't recognise?",
    a: 'For WhatsApp bots, the bot stays silent for unrecognised messages. Add a broad trigger like "help" or use a fallback flow. For website bots, the "no-match message" is shown (configurable in Widget Settings).' },
  { q: 'How do I delete a bot?',
    a: 'In the Dashboard, select the bot, click Settings, scroll to the bottom and click "Delete bot". This is permanent and removes all flows, analytics, and conversation data.' },
];

/* ─── Shared style atoms ─────────────────────────────────────────── */
const input: React.CSSProperties = {
  background:'var(--bg-input)',border:'1px solid var(--card-border)',borderRadius:8,
  padding:'10px 14px',color:'var(--text-primary)',fontSize:14,width:'100%',
  boxSizing:'border-box',outline:'none',fontFamily:'inherit',
};
const textarea: React.CSSProperties = {
  ...input,resize:'vertical',minHeight:120,
};
const primaryBtn: React.CSSProperties = {
  background:'#5B4FE8',color:'white',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,border:'none',width:'100%',cursor:'pointer',minHeight:44,
};
const ghostBtn: React.CSSProperties = {
  background:'var(--bg-element)',color:'var(--text-primary)',
  border:'1px solid var(--card-border)',borderRadius:9,padding:'12px',
  fontSize:14,fontWeight:600,cursor:'pointer',width:'100%',minHeight:44,
};
const card: React.CSSProperties = {
  borderRadius:12,background:'var(--bg-card)',border:'1px solid var(--card-border)',
  boxShadow:'var(--card-shadow)',marginBottom:12,padding:16,
};
const sectionLabel: React.CSSProperties = {
  fontSize:11,color:'var(--text-faint)',textTransform:'uppercase',
  letterSpacing:'0.08em',marginBottom:6,fontWeight:700,display:'block',
};

/* ════════════════════════════════════════════════════════════════ */
export default function SupportMobile() {
  const navigate = useNavigate();

  const [user, setUser]         = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'new'|'open'|'closed'|'faq'>('new');
  const [openFaq, setOpenFaq]   = useState<number|null>(null);

  /* Ticket list */
  const [tickets, setTickets]   = useState<Ticket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket|null>(null);

  /* New ticket form */
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [subject, setSubject]   = useState('');
  const [priority, setPriority] = useState('normal');
  const [category, setCategory] = useState('General');
  const [message, setMessage]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [formError, setFormError]   = useState('');

  /* Auth */
  useEffect(()=>{
    const unsub = auth.onAuthStateChanged(async u=>{
      setUser(u);
      if(u){
        setName(u.displayName||'');
        setEmail(u.email||'');
        loadTickets(u.uid);
      }
    });
    return unsub;
  },[]);

  const loadTickets = async(userId:string)=>{
    setTicketsLoading(true);
    try{
      const q = query(ref(db,'support_tickets'),orderByChild('userId'),equalTo(userId));
      const snap = await get(q);
      const list: Ticket[] = [];
      snap.forEach(c=>{ list.push({id:c.key!,...c.val()}); });
      list.sort((a,b)=>b.createdAt-a.createdAt);
      setTickets(list);
    }catch(e){ console.error(e); }
    finally{ setTicketsLoading(false); }
  };

  const handleSubmit = async()=>{
    if(!name.trim()||!email.trim()||!subject.trim()||!message.trim()){
      setFormError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true); setFormError('');

    const ticket: Omit<Ticket,'id'> = {
      name:name.trim(), email:email.trim(), subject:subject.trim(),
      priority, message:message.trim(),
      userId:user?.uid||null, status:'open', createdAt:Date.now(),
    };

    let saved = false;
    try{
      const ticketRef = push(ref(db,'support_tickets'));
      await set(ticketRef,{id:ticketRef.key,...ticket});
      saved = true;
    }catch(e){ console.warn('Firebase write failed:',e); }

    if(!saved && API_BASE){
      try{
        const res = await fetch(`${API_BASE}/api/support-ticket`,{
          method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(ticket),
        });
        if(res.ok) saved=true;
      }catch(e){ console.warn('API fallback failed:',e); }
    }

    if(!saved){
      const body=`Name: ${ticket.name}\nEmail: ${ticket.email}\nPriority: ${ticket.priority}\n\n${ticket.message}`;
      window.open(`mailto:${companyEmail}?subject=${encodeURIComponent(ticket.subject)}&body=${encodeURIComponent(body)}`,'_blank');
    }

    setSubmitting(false);
    setSubmitted(true);
    if(user?.uid) loadTickets(user.uid);
  };

  const resetForm=()=>{
    setSubject(''); setMessage(''); setPriority('normal');
    setCategory('General'); setSubmitted(false); setFormError('');
  };

  const priorityColor=(p:string)=>
    p==='high'?'#ef4444':p==='normal'?'#5B4FE8':'#64748b';

  const fmtDate=(ts:number)=>{
    const d=new Date(ts);
    const pad=(n:number)=>String(n).padStart(2,'0');
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
  };

  /* ── Ticket detail view ── */
  if(selectedTicket){
    const t=selectedTicket;
    return(
      <MobileLayout
        title={t.subject}
        onBack={()=>setSelectedTicket(null)}
      >
        <div style={{padding:16}}>
          {/* Header card */}
          <div style={card}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
              <span style={{
                borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,
                background:t.status==='open'?'rgba(91,79,232,0.15)':'rgba(100,116,139,0.15)',
                color:t.status==='open'?'#818cf8':'#64748b',textTransform:'uppercase',
              }}>{t.status}</span>
              <span style={{
                borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,
                background:priorityColor(t.priority)+'22',color:priorityColor(t.priority),
                textTransform:'uppercase',
              }}>{t.priority}</span>
            </div>
            <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>{t.subject}</div>
            <div style={{fontSize:12,color:'var(--text-muted)'}}>Opened {fmtDate(t.createdAt)}</div>
          </div>

          {/* Message thread */}
          <div style={{fontSize:11,color:'var(--text-faint)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,marginTop:8}}>Conversation</div>

          {/* User's message (right-aligned, purple) */}
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
            <div style={{
              maxWidth:'80%',background:'#5B4FE8',color:'white',
              borderRadius:'18px 18px 4px 18px',padding:'10px 14px',fontSize:14,lineHeight:1.6,
            }}>
              <div style={{fontSize:12,opacity:0.8,marginBottom:4,fontWeight:600}}>
                {t.name}
              </div>
              <div>{t.message}</div>
              <div style={{fontSize:11,opacity:0.7,marginTop:4,textAlign:'right'}}>{fmtDate(t.createdAt)}</div>
            </div>
          </div>

          {/* Support placeholder reply */}
          {t.status==='open' && (
            <div style={{display:'flex',justifyContent:'flex-start',marginBottom:12}}>
              <div style={{
                maxWidth:'80%',background:'var(--bg-element)',color:'var(--text-primary)',
                borderRadius:'18px 18px 18px 4px',padding:'10px 14px',fontSize:14,lineHeight:1.6,
              }}>
                <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,fontWeight:600}}>
                  {companyName} Support
                </div>
                <div style={{color:'var(--text-muted)',fontStyle:'italic'}}>
                  We've received your message and will reply within 24 hours.
                </div>
              </div>
            </div>
          )}

          {/* Reply box (UI only — full reply threading requires backend) */}
          <div style={{marginTop:16,...card}}>
            <div style={sectionLabel}>Reply</div>
            <textarea
              placeholder="Type your reply..."
              style={textarea}
              readOnly
            />
            <div style={{marginTop:10,fontSize:12,color:'var(--text-muted)'}}>
              To reply, email us at <a href={`mailto:${companyEmail}?subject=Re: ${encodeURIComponent(t.subject)}`} style={{color:'#818cf8',textDecoration:'none'}}>{companyEmail}</a>
            </div>
          </div>
        </div>
      </MobileLayout>
    );
  }

  /* ── Tab content ── */
  const openTickets   = tickets.filter(t=>t.status==='open');
  const closedTickets = tickets.filter(t=>t.status==='closed');

  return(
    <MobileLayout title="Support">
      {/* Tabs */}
      <div style={{
        display:'flex',borderBottom:'1px solid var(--border)',background:'var(--bg-sidebar)',
        overflowX:'auto',flexShrink:0,
      }}>
        {([
          {id:'new'   as const,label:'New Ticket'},
          {id:'open'  as const,label:`Open (${openTickets.length})`},
          {id:'closed'as const,label:'Closed'},
          {id:'faq'   as const,label:'FAQ'},
        ]).map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
            flex:1,padding:'12px 4px',border:'none',cursor:'pointer',fontSize:13,fontWeight:600,
            background:'transparent',
            color:activeTab===tab.id?'#5B4FE8':'var(--text-muted)',
            borderBottom:activeTab===tab.id?'2px solid #5B4FE8':'2px solid transparent',
            whiteSpace:'nowrap',minHeight:44,
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{padding:16}}>

        {/* ── NEW TICKET TAB ── */}
        {activeTab==='new' && (
          <>
            {submitted ? (
              <div style={{...card,textAlign:'center',padding:'40px 16px'}}>
                <div style={{fontSize:48,marginBottom:16}}>✅</div>
                <div style={{fontWeight:700,fontSize:18,marginBottom:8}}>Message sent!</div>
                <div style={{color:'var(--text-muted)',fontSize:14,marginBottom:24,lineHeight:1.6}}>
                  Thanks for reaching out. We'll reply to <strong style={{color:'var(--text-primary)'}}>{email}</strong> within 24 hours.
                </div>
                <button onClick={()=>{ resetForm(); setActiveTab('open'); }} style={primaryBtn}>
                  View my tickets
                </button>
                <button onClick={resetForm} style={{...ghostBtn,marginTop:10}}>Send another</button>
              </div>
            ):(
              <>
                <div style={{...card,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>Send us a message</div>
                  <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.5}}>
                    We respond within 24 hours. Business and Agency plans get priority support.
                  </div>
                </div>

                {formError && (
                  <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',color:'#ef4444',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:14}}>
                    {formError}
                  </div>
                )}

                {/* Name */}
                <div style={{marginBottom:14}}>
                  <div style={sectionLabel}>Your name *</div>
                  <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={input}/>
                </div>

                {/* Email */}
                <div style={{marginBottom:14}}>
                  <div style={sectionLabel}>Email *</div>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" style={input}/>
                </div>

                {/* Subject */}
                <div style={{marginBottom:14}}>
                  <div style={sectionLabel}>Subject *</div>
                  <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="What's the issue about?" style={input}/>
                </div>

                {/* Category */}
                <div style={{marginBottom:14}}>
                  <div style={sectionLabel}>Category</div>
                  <div style={{display:'flex',gap:8}}>
                    {['Billing','Technical','General'].map(c=>(
                      <button key={c} onClick={()=>setCategory(c)} style={{
                        flex:1,padding:'8px 4px',borderRadius:8,border:`1px solid ${category===c?'#5B4FE8':'var(--card-border)'}`,
                        background:category===c?'rgba(91,79,232,0.15)':'var(--bg-element)',
                        color:category===c?'#a5b4fc':'var(--text-muted)',cursor:'pointer',fontSize:13,fontWeight:600,minHeight:40,
                      }}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div style={{marginBottom:14}}>
                  <div style={sectionLabel}>Priority</div>
                  <div style={{display:'flex',gap:8}}>
                    {[
                      {value:'low',label:'Low',color:'#64748b'},
                      {value:'normal',label:'Normal',color:'#5B4FE8'},
                      {value:'high',label:'High',color:'#ef4444'},
                    ].map(p=>(
                      <button key={p.value} onClick={()=>setPriority(p.value)} style={{
                        flex:1,padding:'8px 4px',borderRadius:8,border:`1px solid ${priority===p.value?p.color:'var(--card-border)'}`,
                        background:priority===p.value?p.color+'22':'var(--bg-element)',
                        color:priority===p.value?p.color:'var(--text-muted)',cursor:'pointer',fontSize:13,fontWeight:600,minHeight:40,
                      }}>{p.label}</button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div style={{marginBottom:20}}>
                  <div style={sectionLabel}>Message *</div>
                  <textarea
                    value={message}
                    onChange={e=>setMessage(e.target.value)}
                    placeholder="Describe your issue in detail. Include your bot name, what you were trying to do, and any error messages you saw."
                    style={textarea}
                  />
                </div>

                <button onClick={handleSubmit} disabled={submitting} style={{...primaryBtn,opacity:submitting?0.6:1}}>
                  {submitting?'Sending...':'Send message'}
                </button>
              </>
            )}

            {/* Contact methods */}
            <div style={{...card,marginTop:16}}>
              <div style={sectionLabel}>Other ways to reach us</div>
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                <a href={`mailto:${companyEmail}`} style={{display:'flex',alignItems:'center',gap:12,padding:12,background:'var(--bg-element)',borderRadius:10,textDecoration:'none',color:'var(--text-primary)'}}>
                  <span style={{fontSize:20}}>📧</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:600}}>Email</div>
                    <div style={{fontSize:12,color:'#818cf8'}}>{companyEmail}</div>
                  </div>
                </a>
                <div style={{display:'flex',alignItems:'center',gap:12,padding:12,background:'var(--bg-element)',borderRadius:10}}>
                  <span style={{fontSize:20}}>⏰</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:600}}>Response time</div>
                    <div style={{fontSize:12,color:'var(--text-muted)'}}>Within 24 hours</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── OPEN TICKETS ── */}
        {activeTab==='open' && (
          <>
            {ticketsLoading && (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:14,padding:'32px 0'}}>Loading...</div>
            )}
            {!ticketsLoading && openTickets.length===0 && (
              <div style={{textAlign:'center',padding:'48px 16px'}}>
                <div style={{fontSize:48,marginBottom:12}}>🎉</div>
                <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>No open tickets</div>
                <div style={{color:'var(--text-muted)',fontSize:14,marginBottom:24,lineHeight:1.6}}>
                  We're here to help! Submit a new ticket if you need assistance.
                </div>
                <button onClick={()=>setActiveTab('new')} style={primaryBtn}>New Ticket</button>
              </div>
            )}
            {!ticketsLoading && openTickets.map((t,i)=>(
              <div key={t.id||i} onClick={()=>setSelectedTicket(t)} style={{...card,cursor:'pointer'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:8}}>
                  <div style={{fontWeight:700,fontSize:14,flex:1,marginRight:8,lineHeight:1.4}}>{t.subject}</div>
                  <span style={{
                    borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,flexShrink:0,
                    background:priorityColor(t.priority)+'22',color:priorityColor(t.priority),textTransform:'uppercase',
                  }}>{t.priority}</span>
                </div>
                <div style={{fontSize:12,color:'var(--text-muted)',display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                  <span style={{
                    background:'rgba(91,79,232,0.15)',color:'#818cf8',borderRadius:10,
                    padding:'2px 8px',fontSize:11,fontWeight:600,
                  }}>open</span>
                  <span>{fmtDate(t.createdAt)}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── CLOSED TICKETS ── */}
        {activeTab==='closed' && (
          <>
            {ticketsLoading && (
              <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:14,padding:'32px 0'}}>Loading...</div>
            )}
            {!ticketsLoading && closedTickets.length===0 && (
              <div style={{textAlign:'center',padding:'48px 16px'}}>
                <div style={{fontSize:48,marginBottom:12}}>📂</div>
                <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>No closed tickets</div>
                <div style={{color:'var(--text-muted)',fontSize:14,lineHeight:1.6}}>
                  Resolved tickets will appear here.
                </div>
              </div>
            )}
            {!ticketsLoading && closedTickets.map((t,i)=>(
              <div key={t.id||i} onClick={()=>setSelectedTicket(t)} style={{...card,cursor:'pointer',opacity:0.8}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:8}}>
                  <div style={{fontWeight:700,fontSize:14,flex:1,marginRight:8,lineHeight:1.4}}>{t.subject}</div>
                  <span style={{
                    borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700,flexShrink:0,
                    background:'rgba(100,116,139,0.15)',color:'#64748b',textTransform:'uppercase',
                  }}>closed</span>
                </div>
                <div style={{fontSize:12,color:'var(--text-muted)'}}>{fmtDate(t.createdAt)}</div>
              </div>
            ))}
          </>
        )}

        {/* ── FAQ TAB ── */}
        {activeTab==='faq' && (
          <>
            <div style={{...card,marginBottom:16}}>
              <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>Frequently asked questions</div>
              <div style={{color:'var(--text-muted)',fontSize:13}}>Quick answers to common problems.</div>
            </div>

            {faqs.map((faq,i)=>(
              <div key={i} style={{...card,padding:0,overflow:'hidden'}}>
                <button
                  onClick={()=>setOpenFaq(openFaq===i?null:i)}
                  style={{
                    width:'100%',padding:'14px 16px',background:openFaq===i?'var(--bg-element)':'transparent',
                    border:'none',cursor:'pointer',display:'flex',justifyContent:'space-between',
                    alignItems:'center',textAlign:'left',gap:12,minHeight:52,
                  }}
                >
                  <span style={{color:'var(--text-primary)',fontWeight:600,fontSize:14,lineHeight:1.4,flex:1}}>
                    {faq.q}
                  </span>
                  <span style={{
                    color:'#5B4FE8',fontSize:20,flexShrink:0,
                    transition:'transform 0.2s',
                    transform:openFaq===i?'rotate(45deg)':'rotate(0)',
                    display:'inline-block',
                  }}>+</span>
                </button>
                {openFaq===i && (
                  <div style={{padding:'0 16px 14px',background:'var(--bg-element)'}}>
                    <p style={{color:'var(--text-secondary)',fontSize:14,lineHeight:1.7,margin:0}}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}

            <div style={{...card,textAlign:'center',marginTop:8}}>
              <div style={{fontSize:32,marginBottom:10}}>🔧</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>Still stuck?</div>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16,lineHeight:1.6}}>
                Check the full documentation for step-by-step guides, or contact us directly.
              </p>
              <button onClick={()=>navigate('/documentation')} style={{...ghostBtn,marginBottom:10}}>
                📚 View docs
              </button>
              <button onClick={()=>setActiveTab('new')} style={primaryBtn}>
                📧 Contact us
              </button>
            </div>
          </>
        )}
      </div>
    </MobileLayout>
  );
}
